import { useNavigate } from "@tanstack/react-router";
import {
  Add,
  Button,
  Drawer,
  Text,
} from "@totalenergiescode/mobility-business-rev-design-system";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { createTidLinkedAccount } from "@/api/graphql/billing/createTidLinkedAccount/api";
import { deleteTidLinkedAccount } from "@/api/graphql/billing/deleteTidLinkedAccount/api";
import { getAccountsLinkableToTid } from "@/api/graphql/billing/getAccountsLinkableToTid/api";
import { getTidLinkedAccounts } from "@/api/graphql/billing/searchTidLinkedAccounts/api";
import type { TidLinkedAccountItem } from "@/api/graphql/billing/searchTidLinkedAccounts/types";
import { Loading } from "@/components/shared/Loading/component";

import { AddTidLinkedAccountDrawer } from "./add/component";
import { DeleteTidLinkedAccountDrawer } from "./delete/component";
import { useTidLinkedAccounts } from "./hooks";
import { fromTidAccountsToColumns, fromTidAccountsToRows } from "./mappers";
import {
  StyledAccountsDetailsTidContainerDiv,
  StyledAccountsDetailsTidHeaderDiv,
  StyledCountText,
  StyledLoveHandles,
  StyledSortableCustomTable,
  StyledTableContainerDiv,
} from "./styles";
import type { AccountsDetailsTidProps } from "./types";

export const AccountsDetailsTid = ({
  accountId,
  getTidLinkedAccountsFn = getTidLinkedAccounts,
  createTidLinkedAccountFn = createTidLinkedAccount,
  deleteTidLinkedAccountFn = deleteTidLinkedAccount,
  getAccountsLinkableToTidFn = getAccountsLinkableToTid,
}: Readonly<AccountsDetailsTidProps>) => {
  const navigate = useNavigate();
  const { i18n, t: tCompanies } = useTranslation("companies");

  const [selectedAccount, setSelectedAccount] =
    useState<TidLinkedAccountItem | null>(null);
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);

  const { data, isLoading, fetchNextPage, isFetchingNextPage } =
    useTidLinkedAccounts({
      getTidLinkedAccountsFn,
      accountId,
      enabled: !!accountId,
    });

  const items = data.items || [];
  const total = Number(data.pagination.total) || 0;
  const remaining = Math.max(0, total - items.length);
  const nextPageCount = Math.min(data.pagination.size, remaining);

  const columns = fromTidAccountsToColumns({ items, tCompanies });

  const rows = fromTidAccountsToRows({
    items,
    tCompanies,
    locale: i18n.language,
    navigate,
    onDelete: (id) =>
      setSelectedAccount(
        items.find((item) => item.linkedAccountId === id) || null,
      ),
  });

  return (
    <StyledAccountsDetailsTidContainerDiv>
      <StyledLoveHandles>
        <StyledAccountsDetailsTidHeaderDiv>
          <Text variant='title3'>
            {tCompanies("companies.tid.sectionTitle")}
          </Text>
          <Button
            variant='primary'
            size='s'
            isLowercase
            leftIcon={<Add width={24} height={24} color='#FFFFFF' />}
            buttonText={tCompanies("companies.tid.actions.add")}
            dataTestId='accounts__account-details-tid_add-account-button'
            onClick={() => setIsAddDrawerOpen(true)}
          />
        </StyledAccountsDetailsTidHeaderDiv>

        <Loading isLoading={isLoading}>
          <StyledTableContainerDiv>
            <Text variant='text2'>
              {tCompanies("companies.tid.count", { count: total })}
            </Text>
            <StyledSortableCustomTable
              canSelectRows={false}
              rows={rows}
              columns={columns}
              loadMoreButton={{
                label: tCompanies("companies.tid.table.loadMore", {
                  count: nextPageCount,
                }),
                onClick: () => {
                  fetchNextPage();
                },
                isDisabled: items.length >= total || isFetchingNextPage,
                dataTestId: "accounts__account-details-tid_load-more-button",
              }}
            />
            <StyledCountText variant='sub3'>
              {tCompanies("companies.tid.table.count", {
                count: items.length,
                total,
              })}
            </StyledCountText>
          </StyledTableContainerDiv>
        </Loading>

        <Drawer
          isOpen={selectedAccount !== null}
          onClose={() => setSelectedAccount(null)}
          width='520px'
        >
          <DeleteTidLinkedAccountDrawer
            titleKey='companies.tid.drawer.title'
            dataTestIdPrefix='tid'
            deleteTidLinkedAccountFn={deleteTidLinkedAccountFn}
            onClose={() => setSelectedAccount(null)}
            selectedAccount={selectedAccount}
          />
        </Drawer>

        <Drawer
          isOpen={isAddDrawerOpen}
          onClose={() => setIsAddDrawerOpen(false)}
          width='520px'
        >
          <AddTidLinkedAccountDrawer
            accountId={accountId}
            titleKey='companies.tid.drawer.add.title'
            dataTestIdPrefix='tid'
            createTidLinkedAccountFn={createTidLinkedAccountFn}
            getAccountsLinkableToTidFn={getAccountsLinkableToTidFn}
            onClose={() => setIsAddDrawerOpen(false)}
          />
        </Drawer>
      </StyledLoveHandles>
    </StyledAccountsDetailsTidContainerDiv>
  );
};
